import React, { useState, useEffect, useMemo, useRef } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { getInstructorAnalytics, getInstructorCycleAnalytics, generateInsights, generateTopicPriorities, generateCycleTopicPriorities, getTestSessionConversation, getCycleSessionConversation, exportTestResultsCsv, exportCycleResultsCsv, getInstructorQAAnalytics, getConversationHistory, getConversationFlags, scoreConversation, autoScoreConversation, postInstructorMessage, scanHallucinations, createHallucinationFlag, updateHallucinationFlag, overrideCycleAiFlag, overrideTestAiFlag, overrideQaAiFlag, patchTestQuestionGrade, deleteTestAttempt, deleteCycleAttempt } from '../services/api';
import '../styles/InstructorAnalytics.css';
import '../styles/LessonPlanPreview.css';

// Normalize text for fuzzy question-to-AI-message matching: lowercase, drop
// punctuation, collapse whitespace. Used to find which AI message introduces
// each question in the stored conversation (only display text is persisted —
// action metadata like question_number is dropped, so we match on text).
const normalizeText = (s) => (s || '')
  .toLowerCase()
  .replace(/[^a-z0-9 ]+/g, ' ')
  .replace(/\s+/g, ' ')
  .trim();

// Slice the conversation into per-question message lists by locating each
// question's intro AI message (the one containing the question text). Falls
// back to an empty slice for any question we can't locate.
function sliceConversationByQuestion(messages, sortedQNums, qTextByNum) {
  if (!messages?.length) return {};
  const startByQ = {};
  let searchFrom = 0;
  for (const qNum of sortedQNums) {
    const qText = normalizeText(qTextByNum[qNum]);
    if (!qText) continue;
    // Use a chunk from the middle of the question (skip leading "what is" etc.
    // which can collide) of up to 50 chars
    const needle = qText.length > 60 ? qText.slice(10, 60) : qText.slice(0, Math.min(qText.length, 50));
    if (!needle || needle.length < 8) continue;
    for (let i = searchFrom; i < messages.length; i++) {
      if (!messages[i].is_ai) continue;
      if (normalizeText(messages[i].message).includes(needle)) {
        startByQ[qNum] = i;
        searchFrom = i + 1;
        break;
      }
    }
  }
  const slices = {};
  for (let i = 0; i < sortedQNums.length; i++) {
    const qNum = sortedQNums[i];
    const start = startByQ[qNum];
    if (start == null) { slices[qNum] = []; continue; }
    let end = messages.length;
    for (let j = i + 1; j < sortedQNums.length; j++) {
      const nxt = startByQ[sortedQNums[j]];
      if (nxt != null) { end = nxt; break; }
    }
    slices[qNum] = messages.slice(start, end);
  }
  return slices;
}

// Count AI messages within a slice that mention a hint. The tutor LLM is
// instructed to surface hints in its response text, so /\bhint\b/i is a
// reasonable proxy when hint actions aren't persisted per-question.
function countHintsInMessages(messages) {
  if (!messages?.length) return 0;
  let n = 0;
  for (const m of messages) {
    if (m.is_ai && /\bhint\b/i.test(m.message || '')) n++;
  }
  return n;
}

function StatCard({ label, value, sub, accent, hint }) {
  return (
    <div className={`ia-stat-card${accent ? ' ia-stat-card--accent' : ''}`} title={hint || undefined}>
      <p className="ia-stat-value">{value ?? '—'}</p>
      <p className="ia-stat-label">
        {label}
        {hint && <span className="ia-stat-info" aria-label={hint}>ⓘ</span>}
      </p>
      {sub && <p className="ia-stat-sub">{sub}</p>}
    </div>
  );
}

// The logged-in instructor — needed to authorize grading / flagging / messaging.
function getCurrentUserId() {
  try { return JSON.parse(localStorage.getItem('user') || 'null')?.id ?? null; }
  catch { return null; }
}

const SEVERITY_LABEL = { high: 'High', medium: 'Medium', low: 'Low' };

// Instructor-only control to permanently delete a student attempt (test or cycle).
// Warns via window.confirm before calling onConfirmDelete (which performs the delete
// + triggers an analytics refresh). Shared by test and cycle attempt blocks.
function AttemptDeleteButton({ instructorId, studentName, onConfirmDelete }) {
  const [deleting, setDeleting] = useState(false);
  if (!instructorId) return null;
  const handle = async () => {
    if (deleting) return;
    const who = studentName ? ` for ${studentName}` : '';
    if (!window.confirm(
      `Delete this attempt${who}? This permanently removes the attempt, its grades, ` +
      `and its chat transcript from the database. This cannot be undone.`
    )) return;
    setDeleting(true);
    try {
      await onConfirmDelete();
      // On success the parent reload unmounts/replaces this block — no need to reset.
    } catch {
      alert('Failed to delete attempt. Please try again.');
      setDeleting(false);
    }
  };
  return (
    <button
      type="button"
      className="ia-attempt-delete-btn"
      disabled={deleting}
      onClick={e => { e.stopPropagation(); handle(); }}
      title="Permanently delete this attempt"
    >
      {deleting ? 'Deleting…' : '🗑 Delete'}
    </button>
  );
}

// Per-AI-message instructor tools: shows any hallucination flags on this message
// and lets the instructor flag it, confirm/dismiss auto flags, or add a correction.
function AiMessageTools({ message, instructorId, flags, onChange }) {
  const [flagging, setFlagging] = useState(false);
  const [claim, setClaim] = useState('');
  const [busy, setBusy] = useState(false);
  const msgFlags = (flags || []).filter(f => f.message_id === message.id && f.status !== 'dismissed');

  const submitFlag = async () => {
    if (!instructorId) return;
    setBusy(true);
    try {
      await createHallucinationFlag(instructorId, message.id, { claim: claim.trim(), severity: 'medium' });
      setClaim(''); setFlagging(false);
      onChange?.();
    } finally { setBusy(false); }
  };

  const setStatus = async (flagId, status) => {
    if (!instructorId) return;
    setBusy(true);
    try { await updateHallucinationFlag(instructorId, flagId, { status }); onChange?.(); }
    finally { setBusy(false); }
  };

  return (
    <div className="ia-msg-tools">
      {msgFlags.map(f => (
        <div key={f.id} className={`ia-hflag ia-hflag--${f.severity || 'medium'}`}>
          <div className="ia-hflag-head">
            <span className="ia-hflag-badge">⚑ {f.source === 'auto' ? 'Auto' : 'Instructor'} flag</span>
            {f.severity && <span className="ia-hflag-sev">{SEVERITY_LABEL[f.severity] || f.severity}</span>}
            <span className={`ia-hflag-status ia-hflag-status--${f.status}`}>{f.status}</span>
          </div>
          {f.claim && <p className="ia-hflag-claim">{f.claim}</p>}
          {f.rationale && <p className="ia-hflag-rationale">{f.rationale}</p>}
          {f.correction && <p className="ia-hflag-correction"><strong>Correction:</strong> {f.correction}</p>}
          {f.status !== 'confirmed' && (
            <button type="button" className="ia-hflag-btn" disabled={busy} onClick={() => setStatus(f.id, 'confirmed')}>Confirm</button>
          )}
          <button type="button" className="ia-hflag-btn ia-hflag-btn--dismiss" disabled={busy} onClick={() => setStatus(f.id, 'dismissed')}>Dismiss</button>
        </div>
      ))}
      {flagging ? (
        <div className="ia-msg-flag-form">
          <textarea
            className="ia-msg-flag-input"
            placeholder="What's wrong with this answer? (optional)"
            value={claim}
            onChange={e => setClaim(e.target.value)}
            rows={2}
          />
          <div className="ia-msg-flag-actions">
            <button type="button" className="ia-hflag-btn" disabled={busy} onClick={submitFlag}>Flag</button>
            <button type="button" className="ia-hflag-btn ia-hflag-btn--dismiss" onClick={() => setFlagging(false)}>Cancel</button>
          </div>
        </div>
      ) : (
        <button type="button" className="ia-flag-msg-btn" onClick={() => setFlagging(true)} title="Flag this answer as a possible hallucination">
          ⚑ Flag
        </button>
      )}
    </div>
  );
}

// Renders a transcript with instructor tools attached to each AI message, plus an
// AI-authorship flag marker on any student message the detector flagged.
// `aiFlagsByMsgId` maps a student message id → its AI-authorship flag. `aiFlagOverrideFn`
// is the mode-specific override API (test or Q&A); defaults to the test endpoint.
function TranscriptMessages({ messages, flags, instructorId, onFlagsChange, aiFlagsByMsgId, onAiFlagOverride, aiFlagOverrideFn = overrideTestAiFlag }) {
  return (
    <div className="ia-conv-messages">
      {messages.map((msg, i) => {
        const authorFlag = aiFlagsByMsgId && msg.id != null ? aiFlagsByMsgId[msg.id] : null;
        const isFlagged = authorFlag && authorFlag.status !== 'overridden';
        return (
          <div key={msg.id ?? i} className={`ia-conv-msg ${msg.is_instructor ? 'ia-conv-msg--instructor' : msg.is_ai ? 'ia-conv-msg--ai' : 'ia-conv-msg--student'}${isFlagged ? ' ia-conv-msg--flagged' : ''}`}>
            <span className="ia-conv-msg-role">{msg.is_instructor ? 'Instructor' : msg.is_ai ? 'PaperBuddy' : 'Student'}</span>
            <p className="ia-conv-msg-text">{msg.message}</p>
            {msg.is_ai && msg.id != null && (
              <AiMessageTools message={msg} instructorId={instructorId} flags={flags} onChange={onFlagsChange} />
            )}
            {authorFlag && (
              <AuthorshipFlagTools
                flag={authorFlag}
                instructorId={instructorId}
                onOverride={onAiFlagOverride}
                overrideFn={aiFlagOverrideFn}
              />
            )}
          </div>
        );
      })}
    </div>
  );
}

const INSIGHTS_FAILURE_PREFIX = 'Failed to generate insights';

function hasSuccessfulInsights(insights, loadingInsights) {
  return Boolean(
    insights?.trim() &&
    !loadingInsights &&
    !insights.startsWith(INSIGHTS_FAILURE_PREFIX),
  );
}

function AnalyticsInsightsPanel({
  insights,
  loadingInsights,
  showInsightsContent,
  onToggleShow,
  onGenerate,
  generateDisabled = false,
  emptyPrompt = 'Get AI-generated recommendations: what to review in class, common misconceptions, and which students need extra support.',
  loadingMessage = 'Analyzing class performance…',
}) {
  const generated = hasSuccessfulInsights(insights, loadingInsights);
  const failed = Boolean(insights?.startsWith(INSIGHTS_FAILURE_PREFIX) && !loadingInsights);
  return (
    <div className={`ia-section ia-section--insights${generated && !showInsightsContent ? ' ia-section--collapsed' : ''}`}>
      <div className="ia-section-header">
        <h4 className="ia-section-title">AI Insights</h4>
        <div className="ia-section-actions">
          {generated && (
            <button
              type="button"
              className="ia-collapse-btn"
              onClick={onToggleShow}
              aria-expanded={showInsightsContent}
            >
              {showInsightsContent ? 'Hide' : 'Show'}
            </button>
          )}
          <button
            type="button"
            className="ia-insights-btn"
            onClick={onGenerate}
            disabled={loadingInsights || generateDisabled}
          >
            {loadingInsights ? 'Analyzing…' : generated ? '↺ Regenerate' : '✦ Generate Insights'}
          </button>
        </div>
      </div>
      {showInsightsContent && (
        <>
          {!generated && !loadingInsights && !failed && (
            <p className="ia-insights-prompt">{emptyPrompt}</p>
          )}
          {failed && (
            <p className="ia-insights-prompt">{insights}</p>
          )}
          {loadingInsights && <div className="ia-insights-loading">{loadingMessage}</div>}
          {generated && (
            <div className="ia-insights-body">
              <ReactMarkdown remarkPlugins={[remarkGfm]}>{insights}</ReactMarkdown>
            </div>
          )}
        </>
      )}
    </div>
  );
}

function QBar({ strong, partial, weak, total }) {
  if (!total) return <span className="ia-bar-empty">No data</span>;
  const pct = (n) => `${Math.round((n / total) * 100)}%`;
  return (
    <div className="ia-bar-wrap" title={`${strong} Strong · ${partial} Partial · ${weak} Weak`}>
      {strong > 0 && <div className="ia-bar-seg ia-bar-strong" style={{ width: pct(strong) }} />}
      {partial > 0 && <div className="ia-bar-seg ia-bar-partial" style={{ width: pct(partial) }} />}
      {weak > 0 && <div className="ia-bar-seg ia-bar-weak" style={{ width: pct(weak) }} />}
    </div>
  );
}

function gradeClass(score) {
  if (score == null) return '';
  if (score >= 80) return 'ia-grade-good';
  if (score >= 60) return 'ia-grade-mid';
  return 'ia-grade-low';
}

function StudentRow({ student, onSelect, isAllTests }) {
  // Single test: show the student's BEST attempt (highest score). All-Tests:
  // average each test's BEST attempt across the tests they took — so retries on
  // any one test don't drag the number down. All attempts remain in the expandable
  // detail; this only changes the headline grade.
  const bestByTest = {};
  for (const a of (student.attempts || [])) {
    if (a.average_grade == null) continue;
    if (bestByTest[a.plan_id] == null || a.average_grade > bestByTest[a.plan_id]) {
      bestByTest[a.plan_id] = a.average_grade;
    }
  }
  const perTestBests = Object.values(bestByTest);
  const overallAvg = perTestBests.length ? perTestBests.reduce((a, b) => a + b, 0) / perTestBests.length : null;
  const displayGrade = isAllTests ? overallAvg : student.best_grade;
  const testsTaken = new Set((student.attempts || []).map(a => a.plan_id)).size;
  return (
    <tr className="ia-student-row ia-q-row--clickable" onClick={() => onSelect(student)}>
      <td className="ia-student-name">
        <span className="ia-expand-icon">▸</span>
        {student.student_name}
        {student.total_attempts > 1 && (
          <span className="ia-attempt-badge">{student.total_attempts}×</span>
        )}
      </td>
      <td>
        <span className={`ia-grade-badge ${gradeClass(displayGrade)}`}>
          {displayGrade != null ? `${Math.round(displayGrade)}%` : '—'}
        </span>
        {/* Improvement (first→latest) is shown only in the detailed student
            popup, not inline here, to keep the row uncluttered. */}
      </td>
      {isAllTests && (
        <td className="ia-td-center">{testsTaken || '—'}</td>
      )}
      <td className="ia-td-center">{student.ai_flags > 0 ? <span className="ia-flag-badge">{student.ai_flags}</span> : '—'}</td>
      <td className="ia-td-center">{student.total_hints_used || '—'}</td>
      <td className="ia-td-muted">
        {student.completed_at
          ? new Date(student.completed_at).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })
          : '—'}
      </td>
    </tr>
  );
}

function QuestionResultCard({ qr, qInfo, questionMessages, hintCount, loadingConv, convAvailable, instructorId, flags, onFlagsChange, aiFlags, onAiFlagOverride, onGradeSaved }) {
  const [expanded, setExpanded] = useState(false);
  const showHintChip = !loadingConv && hintCount > 0;
  // AI-authorship flags raised on this question's answers, keyed by message id.
  const aiFlagsByMsgId = useMemo(() => {
    const m = {};
    for (const f of (aiFlags || [])) m[f.message_id] = f;
    return m;
  }, [aiFlags]);
  const activeAiFlagCount = (aiFlags || []).filter(f => f.status !== 'overridden').length;

  // Local view of the (possibly overridden) grade so an edit reflects immediately.
  const [editing, setEditing] = useState(false);
  const [edited, setEdited] = useState(qr.graded_by_instructor || false);
  const [numGrade, setNumGrade] = useState(qr.effective_numerical_grade ?? qr.numerical_grade ?? 0);
  const [catGrade, setCatGrade] = useState(qr.effective_categorical_grade ?? qr.categorical_grade ?? '');
  const [gradeNote, setGradeNote] = useState(qr.instructor_note || '');
  const [savingGrade, setSavingGrade] = useState(false);

  const saveGrade = async () => {
    if (!instructorId || qr.result_id == null) return;
    setSavingGrade(true);
    try {
      await patchTestQuestionGrade(instructorId, qr.result_id, {
        numericalGrade: Number(numGrade),
        categoricalGrade: catGrade,
        note: gradeNote,
      });
      setEdited(true);
      setEditing(false);
      // Refresh analytics so the attempt average, student grade, class average,
      // and grade-distribution chart reflect the new grade (the per-question
      // badge above already updates from local state). The backend recomputes
      // session.average_grade from effective grades on this PATCH.
      onGradeSaved?.();
    } finally { setSavingGrade(false); }
  };

  return (
    <div className="ia-student-response-card">
      <div className="ia-student-response-header">
        <span className="ia-q-popup-num">Q{qr.question_number}</span>
        {qInfo?.question_type && (
          <span className={`question-type-badge ${qInfo.question_type === 'mcq' ? 'mcq' : qInfo.question_type === 'counterfactual' ? 'counterfactual' : 'short-answer'}`}>
            {qInfo.question_type === 'mcq' ? 'MCQ' : qInfo.question_type === 'counterfactual' ? 'Counterfactual' : 'Short Answer'}
          </span>
        )}
        <span className={`ia-grade-badge ${gradeClass(numGrade)} ia-response-grade`}>
          {catGrade || `${numGrade}%`}
        </span>
        {edited && <span className="ia-edited-badge" title="Grade overridden by instructor">edited</span>}
        {instructorId && qr.result_id != null && (
          <button type="button" className="ia-edit-grade-btn" onClick={() => setEditing(v => !v)}>
            {editing ? 'Cancel' : 'Edit grade'}
          </button>
        )}
        {/* Auto MCQ answer-key check — hidden once an instructor overrides the
            grade, since their judgment is authoritative and the stale check
            would contradict the new grade (e.g. "Strong" + "✗ Incorrect"). */}
        {!edited && qr.is_correct === true && (
          <span className="ia-correct-chip ia-correct-chip--yes" title="Answer-key check (MCQ)">✓ Correct</span>
        )}
        {!edited && qr.is_correct === false && (
          <span className="ia-correct-chip ia-correct-chip--no" title="Answer-key check (MCQ)">✗ Incorrect</span>
        )}
        {showHintChip && (
          <span className="ia-hint-chip" title="Hints given for this question">
            {hintCount} hint{hintCount !== 1 ? 's' : ''}
          </span>
        )}
        {activeAiFlagCount > 0 && (
          <span className="ia-flag-badge" title="AI-use flag(s) raised on this question — expand the exchange to review or override">
            ⚑ {activeAiFlagCount} AI flag{activeAiFlagCount !== 1 ? 's' : ''}
          </span>
        )}
      </div>
      {editing && (
        <div className="ia-grade-edit">
          <label className="ia-qa-label">Grade %
            <input type="number" min="0" max="100" className="ia-qa-score-input"
              value={numGrade} onChange={e => setNumGrade(e.target.value)} />
          </label>
          <label className="ia-qa-label">Category
            <select className="ia-grade-cat-select" value={catGrade} onChange={e => setCatGrade(e.target.value)}>
              <option value="">—</option>
              <option value="Strong">Strong</option>
              <option value="Partial">Partial</option>
              <option value="Weak">Weak</option>
              <option value="Missing">Missing</option>
            </select>
          </label>
          <input type="text" className="ia-qa-note-input" placeholder="Note (optional)"
            value={gradeNote} onChange={e => setGradeNote(e.target.value)} />
          <button type="button" className="ia-hflag-btn" disabled={savingGrade} onClick={saveGrade}>
            {savingGrade ? 'Saving…' : 'Save grade'}
          </button>
        </div>
      )}
      {qInfo?.question_text && (
        <p className="ia-student-q-text">{qInfo.question_text}</p>
      )}
      <div className="ia-student-response-body">
        {qr.student_response
          ? <p className="ia-student-response-text">{qr.student_response}</p>
          : <p className="ia-student-response-empty">No response recorded</p>
        }
      </div>
      {qr.llm_feedback && (qr.llm_feedback.strengths || qr.llm_feedback.weaknesses || qr.llm_feedback.improvements) && (
        <div className="ia-feedback-block">
          {qr.llm_feedback.strengths && (
            <p className="ia-feedback-item ia-feedback-item--strength">
              <span className="ia-feedback-label">Strengths</span> {qr.llm_feedback.strengths}
            </p>
          )}
          {qr.llm_feedback.weaknesses && (
            <p className="ia-feedback-item ia-feedback-item--weakness">
              <span className="ia-feedback-label">Weaknesses</span> {qr.llm_feedback.weaknesses}
            </p>
          )}
          {qr.llm_feedback.improvements && (
            <p className="ia-feedback-item ia-feedback-item--improve">
              <span className="ia-feedback-label">Improvements</span> {qr.llm_feedback.improvements}
            </p>
          )}
        </div>
      )}
      {convAvailable && (
        <button
          type="button"
          className="ia-conv-toggle-btn"
          onClick={() => setExpanded(v => !v)}
        >
          {expanded ? '▲ Hide full exchange' : '▾ View full exchange'}
        </button>
      )}
      {expanded && (
        <>
          {loadingConv && <div className="ia-conv-messages"><p className="ia-conv-loading">Loading…</p></div>}
          {!loadingConv && (!questionMessages || questionMessages.length === 0) && (
            <div className="ia-conv-messages"><p className="ia-conv-empty">No exchange found for this question.</p></div>
          )}
          {!loadingConv && questionMessages && questionMessages.length > 0 && (
            <TranscriptMessages
              messages={questionMessages}
              flags={flags}
              instructorId={instructorId}
              onFlagsChange={onFlagsChange}
              aiFlagsByMsgId={aiFlagsByMsgId}
              onAiFlagOverride={onAiFlagOverride}
            />
          )}
        </>
      )}
    </div>
  );
}

// Wraps an attempt's question results: fetches the conversation lazily when
// the attempt is expanded, slices it per question, and passes each slice +
// hint count down to the cards.
function AttemptBlock({ attempt, attemptNum, defaultExpanded, allQuestionInfos, instructorId, studentName, onAttemptDeleted, onFlagsChange }) {
  const [expanded, setExpanded] = useState(!!defaultExpanded);
  const [convMessages, setConvMessages] = useState(null);
  const [aiFlags, setAiFlags] = useState([]);
  const [loadingConv, setLoadingConv] = useState(false);

  useEffect(() => {
    // NOTE: do NOT add `loadingConv` to the deps (or the guard). Toggling it
    // inside the effect would re-trigger the effect, whose cleanup sets
    // cancelled=true on the in-flight fetch — so the `.finally` that clears
    // loadingConv gets skipped and the card hangs on "Loading…" forever.
    // convMessages !== null already prevents a redundant refetch once loaded.
    if (!expanded || !attempt.session_id || convMessages !== null) return undefined;
    let cancelled = false;
    setLoadingConv(true);
    getTestSessionConversation(attempt.session_id)
      .then(d => { if (!cancelled) { setConvMessages(d.messages || []); setAiFlags(d.ai_flags || []); } })
      .catch(() => { if (!cancelled) setConvMessages([]); })
      .finally(() => { if (!cancelled) setLoadingConv(false); });
    return () => { cancelled = true; };
  }, [expanded, attempt.session_id, convMessages]);

  // Reflect an override locally right away, then let the parent refresh the
  // background analytics counts (overview / student row).
  const handleAiFlagOverride = (flagId, newStatus) => {
    setAiFlags(prev => prev.map(f => (f.id === flagId ? { ...f, status: newStatus } : f)));
    onFlagsChange?.();
  };

  const aiFlagsByQuestion = useMemo(() => {
    const m = {};
    for (const f of aiFlags) {
      if (!m[f.question_number]) m[f.question_number] = [];
      m[f.question_number].push(f);
    }
    return m;
  }, [aiFlags]);

  const sortedQResults = useMemo(
    () => [...(attempt.question_results || [])].sort((a, b) => a.question_number - b.question_number),
    [attempt.question_results]
  );

  const slices = useMemo(() => {
    if (!convMessages) return {};
    // Deterministic grouping: with per-question threading, each message carries its
    // thread's question_number (0-based; null for the root/init thread). Group by
    // that — 0-based qnum maps to the 1-based question number used by results. This
    // is reliable, unlike fuzzy-matching question text against AI messages.
    if (convMessages.some(m => m.question_number != null)) {
      const out = {};
      for (const m of convMessages) {
        if (m.question_number == null) continue; // root/init thread — not a question
        const qn = m.question_number + 1;
        if (!out[qn]) out[qn] = [];
        out[qn].push(m);
      }
      return out;
    }
    // Legacy fallback for runs whose threads weren't question-tagged.
    const qTextByNum = {};
    for (const q of (allQuestionInfos || [])) qTextByNum[q.question_number] = q.question_text || '';
    return sliceConversationByQuestion(convMessages, sortedQResults.map(q => q.question_number), qTextByNum);
  }, [convMessages, sortedQResults, allQuestionInfos]);

  return (
    <div className={`ia-attempt-block${expanded ? ' ia-attempt-block--open' : ''}`}>
      <button
        type="button"
        className="ia-attempt-header ia-attempt-header--toggle"
        onClick={() => setExpanded(e => !e)}
        aria-expanded={expanded}
      >
        <span className="ia-attempt-caret">{expanded ? '▾' : '▸'}</span>
        <span className="ia-attempt-label">Attempt {attemptNum} · {attempt.plan_label}</span>
        <span className="ia-attempt-date">
          {attempt.completed_at ? new Date(attempt.completed_at).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' }) : '—'}
        </span>
        <span className={`ia-grade-badge ${gradeClass(attempt.average_grade)}`}>
          {attempt.average_grade != null ? `${Math.round(attempt.average_grade)}%` : '—'}
        </span>
        <AttemptDeleteButton
          instructorId={instructorId}
          studentName={studentName}
          onConfirmDelete={async () => {
            await deleteTestAttempt(instructorId, attempt.session_id);
            onAttemptDeleted?.();
          }}
        />
      </button>
      {expanded && (
        <div className="ia-attempt-body">
          {(attempt.question_results || []).map(qr => {
            const qInfo = (allQuestionInfos || []).find(q => q.question_number === qr.question_number);
            const slice = slices[qr.question_number] || [];
            return (
              <QuestionResultCard
                key={qr.question_number}
                qr={qr}
                qInfo={qInfo}
                questionMessages={slice}
                hintCount={countHintsInMessages(slice)}
                loadingConv={loadingConv}
                convAvailable={!!attempt.session_id}
                instructorId={instructorId}
                flags={[]}
                onFlagsChange={undefined}
                aiFlags={aiFlagsByQuestion[qr.question_number] || []}
                onAiFlagOverride={handleAiFlagOverride}
                onGradeSaved={onFlagsChange}
              />
            );
          })}
        </div>
      )}
    </div>
  );
}

// ── Cycle analytics ────────────────────────────────────────────────────────

function CycleProgressBar({ completed, total }) {
  const pct = total > 0 ? Math.round((completed / total) * 100) : 0;
  const fillClass = pct >= 100 ? 'ia-cprog-fill ia-cprog-fill--full'
    : pct >= 60 ? 'ia-cprog-fill ia-cprog-fill--mid'
    : 'ia-cprog-fill ia-cprog-fill--low';
  return (
    <div className="ia-cprog-wrap" title={`${completed}/${total} cycles · ${pct}%`}>
      <div className="ia-cprog-track">
        <div className={fillClass} style={{ width: `${pct}%` }} />
      </div>
      <span className="ia-cprog-label">{completed}/{total || '?'}</span>
    </div>
  );
}

function CycleStudentRow({ student, onSelect }) {
  const completed = student.latest_cycles_completed;
  const total = student.latest_total_cycles;
  return (
    <tr className="ia-student-row ia-q-row--clickable" onClick={() => onSelect(student)}>
      <td className="ia-student-name">
        <span className="ia-expand-icon">▸</span>
        {student.student_name}
        {student.total_attempts > 1 && (
          <span className="ia-attempt-badge">{student.total_attempts}×</span>
        )}
      </td>
      <td className="ia-cprog-cell">
        <CycleProgressBar completed={completed} total={total} />
      </td>
      <td className="ia-td-center">
        {student.completed_runs > 0
          ? <span className="ia-status-pill ia-status-pill--done">Finished</span>
          : <span className="ia-status-pill ia-status-pill--inprogress">In progress</span>}
      </td>
      <td className="ia-td-center">{student.ai_flags > 0 ? <span className="ia-flag-badge">{student.ai_flags}</span> : '—'}</td>
      <td className="ia-td-muted">
        {student.last_activity
          ? new Date(student.last_activity).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })
          : '—'}
      </td>
    </tr>
  );
}

// Match "Cycle N:" or "Cycle N -" headers that the cycle LLM emits at the
// start of each cycle. Anchored to a line start so we don't catch incidental
// mentions like "in cycle 3 we...". Case-insensitive; allows a separator that
// is colon, dash, en/em dash, or just whitespace before the title.
const CYCLE_MARKER_RE = /(^|\n)\s*(?:#+\s*)?\*{0,2}\s*Cycle\s+(\d+)\s*(?:[:\-–—]|\s)/i;

function findCycleStarts(messages) {
  // Walk AI messages in order; record the index of the first message that
  // mentions "Cycle N" for each N. Returns { [cycleNumber]: messageIndex }.
  const starts = {};
  for (let i = 0; i < messages.length; i++) {
    const m = messages[i];
    if (!m.is_ai) continue;
    const text = m.message || '';
    // A single message can introduce multiple cycles if the LLM batches them,
    // but that's rare — pick the first cycle number this message announces.
    const match = text.match(CYCLE_MARKER_RE);
    if (!match) continue;
    const cycleNum = parseInt(match[2], 10);
    if (Number.isNaN(cycleNum)) continue;
    if (starts[cycleNum] == null) starts[cycleNum] = i;
  }
  return starts;
}

function sliceMessagesByCycle(messages, totalCycles) {
  // Returns [{ cycleNumber, messages, startIdx, endIdx }] for each cycle the
  // student reached. Pre-cycle preamble (system "Start the cycling session"
  // turn) is dropped — it doesn't belong to any cycle.
  if (!messages?.length) return [];
  const starts = findCycleStarts(messages);
  const cycleNums = Object.keys(starts).map(Number).sort((a, b) => a - b);
  if (cycleNums.length === 0) {
    // Couldn't find any markers — return everything as one bucket so the
    // instructor still sees the transcript.
    return [{ cycleNumber: null, messages, startIdx: 0, endIdx: messages.length }];
  }
  const slices = [];
  for (let i = 0; i < cycleNums.length; i++) {
    const cn = cycleNums[i];
    const start = starts[cn];
    const end = i + 1 < cycleNums.length ? starts[cycleNums[i + 1]] : messages.length;
    slices.push({ cycleNumber: cn, messages: messages.slice(start, end), startIdx: start, endIdx: end });
  }
  // If the plan has more cycles than markers we found, the missing ones simply
  // weren't reached — caller can render placeholder rows.
  void totalCycles;
  return slices;
}

// Per-flag instructor control on a flagged student answer (cycle OR test): shows
// the detector's reason and lets the instructor override a false positive (or
// restore it). `overrideFn(instructorId, flagId, status)` is the mode-specific API
// call. Mirrors the override pattern of AiMessageTools.
function AuthorshipFlagTools({ flag, instructorId, onOverride, overrideFn }) {
  const [busy, setBusy] = useState(false);
  const overridden = flag.status === 'overridden';

  const setStatus = async (newStatus) => {
    if (!instructorId) return;
    setBusy(true);
    try {
      await overrideFn(instructorId, flag.id, newStatus);
      onOverride?.(flag.id, newStatus);
    } finally { setBusy(false); }
  };

  return (
    <div className={`ia-hflag ia-hflag--high${overridden ? ' ia-hflag--overridden' : ''}`}>
      <div className="ia-hflag-head">
        <span className="ia-hflag-badge">⚑ AI-use flag</span>
        {flag.confidence != null && (
          <span className="ia-hflag-sev">{Math.round(flag.confidence * 100)}% conf.</span>
        )}
        <span className={`ia-hflag-status ia-hflag-status--${overridden ? 'dismissed' : 'open'}`}>
          {overridden ? 'overridden' : 'active'}
        </span>
      </div>
      {flag.reason && <p className="ia-hflag-rationale">{flag.reason}</p>}
      {instructorId && (
        overridden ? (
          <button type="button" className="ia-hflag-btn" disabled={busy} onClick={() => setStatus('active')}>
            Restore flag
          </button>
        ) : (
          <button type="button" className="ia-hflag-btn ia-hflag-btn--dismiss" disabled={busy} onClick={() => setStatus('overridden')}>
            Override (false positive)
          </button>
        )
      )}
    </div>
  );
}

function CycleConversationBlock({ cycleNumber, cycleTitle, messages, savedMessageCount, cycleFlags, instructorId, onFlagOverride }) {
  const [expanded, setExpanded] = useState(false);
  const aiMsgs = messages.filter(m => m.is_ai);
  const studentMsgs = messages.filter(m => !m.is_ai);
  const hintCount = countHintsInMessages(messages);
  // Real per-message AI-authorship flags for this cycle (CycleAiFlag records).
  // Only ACTIVE flags count — an instructor override (false positive) excludes it.
  const flagByMsgId = useMemo(() => {
    const m = {};
    for (const f of (cycleFlags || [])) m[f.message_id] = f;
    return m;
  }, [cycleFlags]);
  const activeFlagCount = (cycleFlags || []).filter(f => f.status !== 'overridden').length;
  // Show the same total here and in the collapsed summary so they never disagree:
  // both derive from the sliced transcript (student + AI), not the separate saved
  // per-cycle counter (which historically produced a mismatching number).
  const exchangeCount = messages.length;
  const displayCount = exchangeCount;
  void savedMessageCount;

  return (
    <div className="ia-cycle-conv">
      <button
        type="button"
        className="ia-cycle-conv-head"
        onClick={() => setExpanded(v => !v)}
        aria-expanded={expanded}
      >
        <span className="ia-cycle-num-badge">C{cycleNumber ?? '?'}</span>
        <span className="ia-cycle-conv-title">{cycleTitle || (cycleNumber != null ? `Cycle ${cycleNumber}` : 'Full transcript')}</span>
        <span className="ia-cycle-conv-stats">
          <span className="ia-cycle-stat"><strong>{studentMsgs.length}</strong> student</span>
          <span className="ia-cycle-stat"><strong>{aiMsgs.length}</strong> AI</span>
          {hintCount > 0 && <span className="ia-hint-chip">{hintCount} hint{hintCount !== 1 ? 's' : ''}</span>}
          {activeFlagCount > 0 && <span className="ia-flag-badge">{activeFlagCount} AI flag{activeFlagCount !== 1 ? 's' : ''}</span>}
        </span>
        <span className="ia-cycle-conv-chevron">{expanded ? '▲' : '▼'}</span>
      </button>
      {expanded && (
        <div className="ia-conv-messages">
          {exchangeCount === 0 && <p className="ia-conv-empty">No messages recorded for this cycle.</p>}
          {messages.map((msg, i) => {
            const flag = msg.id != null ? flagByMsgId[msg.id] : null;
            const isFlagged = flag && flag.status !== 'overridden';
            return (
              <div key={msg.id ?? i} className={`ia-conv-msg ${msg.is_ai ? 'ia-conv-msg--ai' : 'ia-conv-msg--student'}${isFlagged ? ' ia-conv-msg--flagged' : ''}`}>
                <span className="ia-conv-msg-role">{msg.is_ai ? 'PaperBuddy' : 'Student'}</span>
                <p className="ia-conv-msg-text">{msg.message}</p>
                {flag && (
                  <AuthorshipFlagTools flag={flag} instructorId={instructorId} onOverride={onFlagOverride} overrideFn={overrideCycleAiFlag} />
                )}
              </div>
            );
          })}
        </div>
      )}
      {!expanded && displayCount > 0 && (
        <p className="ia-cycle-conv-summary">{displayCount} message{displayCount !== 1 ? 's' : ''} exchanged · click to view transcript</p>
      )}
    </div>
  );
}

function CycleAttemptBlock({ attempt, attemptNum, cycleBreakdown, instructorId, onFlagsChange, studentName, onAttemptDeleted }) {
  const [conv, setConv] = useState(null);
  const [flags, setFlags] = useState([]);
  const [loadingConv, setLoadingConv] = useState(false);
  const [convError, setConvError] = useState(null);
  const cmc = attempt.cycle_message_counts || [];

  useEffect(() => {
    if (!attempt.session_id) return undefined;
    let cancelled = false;
    setLoadingConv(true);
    setConvError(null);
    setConv(null);
    setFlags([]);
    getCycleSessionConversation(attempt.session_id)
      .then(d => { if (!cancelled) { setConv(d.messages || []); setFlags(d.ai_flags || []); } })
      .catch(e => { if (!cancelled) { setConv([]); setConvError(e?.message || 'Failed to load conversation'); } })
      .finally(() => { if (!cancelled) setLoadingConv(false); });
    return () => { cancelled = true; };
  }, [attempt.session_id]);

  // Reflect an override locally (flip status) right away, then let the parent
  // refresh the background analytics counts.
  const handleFlagOverride = (flagId, newStatus) => {
    setFlags(prev => prev.map(f => (f.id === flagId ? { ...f, status: newStatus } : f)));
    onFlagsChange?.();
  };

  const slices = useMemo(
    () => conv ? sliceMessagesByCycle(conv, attempt.total_cycles) : [],
    [conv, attempt.total_cycles]
  );
  const titleByCycle = useMemo(() => {
    const m = {};
    for (const c of (cycleBreakdown || [])) m[c.cycle_number] = c.title;
    return m;
  }, [cycleBreakdown]);

  // Build a stable list: one row per cycle the student reached, plus any
  // missing ones at the end as placeholders.
  const reachedNums = new Set(slices.map(s => s.cycleNumber).filter(n => n != null));
  const totalCycles = attempt.total_cycles || slices.length;
  const placeholders = [];
  for (let n = 1; n <= totalCycles; n++) {
    if (!reachedNums.has(n)) placeholders.push(n);
  }

  return (
    <div className="ia-attempt-block">
      <div className="ia-attempt-header">
        <span className="ia-attempt-label">Attempt {attemptNum} · {attempt.plan_label}</span>
        <span className="ia-attempt-date">
          {attempt.updated_at ? new Date(attempt.updated_at).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' }) : '—'}
        </span>
        <span className="ia-attempt-progress-chip">
          {attempt.cycles_completed}/{attempt.total_cycles || '?'} cycles · {attempt.completion_pct}%
        </span>
        {attempt.status === 'completed' && (
          <span className="ia-status-pill ia-status-pill--done">Finished</span>
        )}
        <AttemptDeleteButton
          instructorId={instructorId}
          studentName={studentName}
          onConfirmDelete={async () => {
            await deleteCycleAttempt(instructorId, attempt.session_id);
            onAttemptDeleted?.();
          }}
        />
      </div>

      {loadingConv && <p className="ia-conv-loading">Loading conversation…</p>}
      {convError && !loadingConv && (
        <p className="ia-conv-empty">{convError}</p>
      )}

      {!loadingConv && !convError && slices.length === 0 && conv && conv.length === 0 && (
        <p className="ia-conv-empty">
          No conversation found for this attempt
          {attempt.chat_index == null ? ' (chat link was not recorded).' : '.'}
        </p>
      )}

      {!loadingConv && slices.length > 0 && (
        <div className="ia-cycle-conv-list">
          {slices.map(s => (
            <CycleConversationBlock
              key={`c-${s.cycleNumber ?? 'all'}`}
              cycleNumber={s.cycleNumber}
              cycleTitle={titleByCycle[s.cycleNumber]}
              messages={s.messages}
              savedMessageCount={s.cycleNumber ? (cmc[s.cycleNumber - 1] || 0) : 0}
              cycleFlags={s.cycleNumber != null
                ? flags.filter(f => f.cycle_number === s.cycleNumber)
                : flags}
              instructorId={instructorId}
              onFlagOverride={handleFlagOverride}
            />
          ))}
          {placeholders.length > 0 && placeholders.map(n => (
            <div key={`ph-${n}`} className="ia-cycle-conv ia-cycle-conv--unreached">
              <div className="ia-cycle-conv-head ia-cycle-conv-head--static">
                <span className="ia-cycle-num-badge">C{n}</span>
                <span className="ia-cycle-conv-title">{titleByCycle[n] || `Cycle ${n}`}</span>
                <span className="ia-cycle-conv-stats">
                  <span className="ia-status-pill ia-status-pill--inprogress">Not reached</span>
                </span>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function CycleAnalyticsView({
  loading,
  data,
  selectedPlanId,
  onSelectPlan,
  selectedStudent,
  onSelectStudent,
  insights,
  loadingInsights,
  showInsightsContent,
  onToggleInsightsShow,
  onGenerateInsights,
  priorities,
  loadingPriorities,
  showPrioritiesContent,
  hasPrioritiesContent,
  onTogglePrioritiesShow,
  onGeneratePriorities,
  onExportCsv,
  onFlagsChange,
}) {
  const [selectedCycle, setSelectedCycle] = useState(null);
  const instructorId = getCurrentUserId();

  if (loading) return <div className="ia-loading">Loading cycle analytics…</div>;
  if (!data) return <div className="ia-loading">No cycle data available.</div>;

  const isAllPlans = selectedPlanId == null && (data.plans?.length || 0) > 0;
  const students = data.students || [];
  const cycleBreakdown = data.cycle_breakdown || [];
  const perPlanBreakdown = data.per_plan_breakdown || [];

  // Drop-off distribution: where students got stuck (across all attempts for the selected plan).
  const dropoffMax = Math.max(1, ...cycleBreakdown.map(c => c.stuck_here || 0));

  return (
    <div className="ia-scroll">
      {/* Overview cards */}
      <div className="ia-stat-row">
        <StatCard
          label="Avg Completion"
          value={data.avg_completion_pct != null ? `${data.avg_completion_pct}%` : null}
          accent={data.avg_completion_pct != null}
        />
        <StatCard label="Students" value={data.total_students} />
        <StatCard label="Full Completions" value={data.total_completed_runs} />
        <StatCard label="AI Flags" value={data.total_ai_flags} />
      </div>

      {students.length === 0 ? (
        <div className="ia-empty">No students have started cycle mode for this paper yet.</div>
      ) : (
        <>
          {/* Per-cycle breakdown — single plan only. Card layout so long
              titles wrap and metrics never get clipped in narrow panels.
              Cards are clickable: opens a popup showing the full cycle plan
              (read sections, core ideas, recommendations, questions). */}
          {!isAllPlans && cycleBreakdown.length > 0 && (
            <div className="ia-section">
              <h4 className="ia-section-title">Per-cycle engagement</h4>
              <p className="ia-section-hint">Per-cycle progress and effort — click a cycle for its plan.</p>
              <div className="ia-cycle-cards">
                {cycleBreakdown.map(c => (
                  <div
                    key={c.cycle_number}
                    className="ia-cycle-card ia-cycle-card--clickable"
                    onClick={() => setSelectedCycle(c)}
                    title="Click to see this cycle's full plan"
                  >
                    <div className="ia-cycle-card-head">
                      <span className="ia-cycle-num-badge">C{c.cycle_number}</span>
                      <span className="ia-cycle-card-title">{c.title}</span>
                      <span className="ia-cycle-card-chevron" aria-hidden="true">›</span>
                    </div>
                    <div className="ia-cycle-card-bar-row">
                      <div className="ia-bar-wrap" title={`${c.reach_pct}% reached`}>
                        <div className="ia-bar-seg ia-bar-strong" style={{ width: `${c.reach_pct}%` }} />
                      </div>
                      <span className="ia-cycle-card-pct">{c.reach_pct}%</span>
                    </div>
                    <div className="ia-cycle-card-stats">
                      <span className="ia-cycle-stat" title="Students who got at least to this cycle">
                        <strong>{c.reached}</strong> reached
                      </span>
                      <span className="ia-cycle-stat" title="Students currently sitting on this cycle without advancing">
                        <strong>{c.stuck_here || '—'}</strong> stuck
                      </span>
                      <span className="ia-cycle-stat" title="Average messages exchanged while completing this cycle">
                        <strong>{c.avg_messages != null ? c.avg_messages : '—'}</strong> avg msgs
                      </span>
                      <span className={`ia-cycle-stat${c.ai_flags > 0 ? ' ia-cycle-stat--warn' : ''}`} title="Active AI-use flags raised in this cycle">
                        <strong>{c.ai_flags || '—'}</strong> AI flag{c.ai_flags === 1 ? '' : 's'}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Per-plan breakdown — all-plans only. Cards mirror per-cycle layout. */}
          {isAllPlans && perPlanBreakdown.length > 0 && (
            <div className="ia-section">
              <h4 className="ia-section-title">Per-plan class performance</h4>
              <p className="ia-section-hint">How each cycle plan stacks up. Click a card to drill in.</p>
              <div className="ia-cycle-cards">
                {perPlanBreakdown.map(p => (
                  <div
                    key={p.plan_id}
                    className="ia-cycle-card ia-cycle-card--clickable"
                    onClick={() => onSelectPlan(p.plan_id)}
                    title="Click to view this plan's details"
                  >
                    <div className="ia-cycle-card-head">
                      <div className="ia-cycle-card-title-block">
                        <div className="ia-cycle-card-title">{p.plan_label}</div>
                        <div className="ia-cycle-card-meta">
                          {p.cycle_count} cycle{p.cycle_count !== 1 ? 's' : ''}
                          {p.created_at && ` · ${new Date(p.created_at).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })}`}
                        </div>
                      </div>
                      {p.avg_completion_pct != null
                        ? <span className={`ia-grade-badge ${p.avg_completion_pct >= 80 ? 'ia-grade-good' : p.avg_completion_pct >= 50 ? 'ia-grade-mid' : 'ia-grade-low'}`}>{p.avg_completion_pct}%</span>
                        : <span className="ia-no-data">N/A</span>}
                    </div>
                    <div className="ia-cycle-card-stats">
                      <span className="ia-cycle-stat"><strong>{p.student_count || '—'}</strong> students</span>
                      <span className="ia-cycle-stat"><strong>{p.total_attempts || '—'}</strong> attempts</span>
                      <span className="ia-cycle-stat"><strong>{p.completed_count || '—'}</strong> finished</span>
                      <span className={`ia-cycle-stat${p.total_ai_flags > 0 ? ' ia-cycle-stat--warn' : ''}`}>
                        <strong>{p.total_ai_flags || '—'}</strong> flags
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Drop-off chart — single plan only, only if there's anything to show */}
          {!isAllPlans && cycleBreakdown.some(c => c.stuck_here > 0) && (
            <div className="ia-section">
              <h4 className="ia-section-title">Drop-off by cycle</h4>
              <p className="ia-section-hint">Students currently stuck at each cycle. Tall bars flag where the plan needs attention.</p>
              <div className="ia-dist-rows">
                {cycleBreakdown.map(c => (
                  <div key={c.cycle_number} className="ia-dist-row">
                    <span className="ia-dist-label">C{c.cycle_number}</span>
                    <div className="ia-dist-track">
                      <div
                        className="ia-dist-fill ia-dist-fill--warn"
                        style={{ width: `${(c.stuck_here / dropoffMax) * 100}%` }}
                      />
                    </div>
                    <span className="ia-dist-count">{c.stuck_here}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Student breakdown */}
          <div className="ia-section">
            <div className="ia-section-header">
              <h4 className="ia-section-title">Student progress</h4>
              <button
                type="button"
                className="ia-export-btn"
                onClick={onExportCsv}
                title="Download cycle progress as CSV"
              >
                ↓ Export CSV
              </button>
            </div>
            <p className="ia-section-hint">
              {isAllPlans
                ? 'Latest cycle run per student across every plan. Click a student to see their attempts.'
                : 'How far each student got. Click a student to see per-cycle engagement.'}
            </p>
            <table className="ia-student-table ia-cycle-student-table">
              <thead>
                <tr>
                  <th>Student</th>
                  <th>Progress</th>
                  <th className="ia-td-center">Status</th>
                  <th className="ia-td-center">Flags</th>
                  <th>Last seen</th>
                </tr>
              </thead>
              <tbody>
                {students.map((s, i) => (
                  <CycleStudentRow
                    key={s.user_id ?? i}
                    student={s}
                    onSelect={onSelectStudent}
                  />
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      {/* Teaching priorities — single plan only, needs cycle breakdown */}
      {!isAllPlans && cycleBreakdown.length > 0 && (
        <div className={`ia-section${hasPrioritiesContent && !showPrioritiesContent ? ' ia-section--collapsed' : ''}`}>
          <div className="ia-section-header">
            <h4 className="ia-section-title">Teaching priorities</h4>
            <div className="ia-section-actions">
              {hasPrioritiesContent && (
                <button
                  type="button"
                  className="ia-collapse-btn"
                  onClick={onTogglePrioritiesShow}
                  aria-expanded={showPrioritiesContent}
                >
                  {showPrioritiesContent ? 'Hide' : 'Show'}
                </button>
              )}
              <button
                type="button"
                className="ia-insights-btn"
                onClick={onGeneratePriorities}
                disabled={loadingPriorities || students.length === 0}
              >
                {loadingPriorities ? 'Analyzing…' : hasPrioritiesContent ? '↺ Refresh' : '✦ Analyze with AI'}
              </button>
            </div>
          </div>
          {showPrioritiesContent && (
            <>
              {!priorities && !loadingPriorities && (
                <p className="ia-insights-prompt">Identify which cycles need the most attention before the next class.</p>
              )}
              {loadingPriorities && <div className="ia-insights-loading">Identifying priority cycles…</div>}
              {priorities && priorities.length === 0 && (
                <p className="ia-insights-prompt">Could not generate priorities. Try again.</p>
              )}
              {priorities && priorities.length > 0 && (
                <div className="ia-priorities-list">
                  {priorities.map((p, i) => (
                    <div key={i} className={`ia-priority-card ia-priority-card--${p.severity}`}>
                      <div className="ia-priority-header">
                        <span className={`ia-priority-badge ia-priority-badge--${p.severity}`}>
                          {p.severity === 'high' ? 'High' : p.severity === 'medium' ? 'Medium' : 'Low'}
                        </span>
                        <p className="ia-priority-topic">{p.topic}</p>
                      </div>
                      <p className="ia-priority-evidence">{p.evidence}</p>
                      <p className="ia-priority-action">→ {p.action}</p>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      )}

      <AnalyticsInsightsPanel
        insights={insights}
        loadingInsights={loadingInsights}
        showInsightsContent={showInsightsContent}
        onToggleShow={onToggleInsightsShow}
        onGenerate={onGenerateInsights}
        generateDisabled={students.length === 0}
      />

      {/* Student popup */}
      {selectedStudent && (
        <div className="ia-q-popup-backdrop" onClick={() => onSelectStudent(null)}>
          <div className="ia-q-popup ia-student-popup" onClick={e => e.stopPropagation()}>
            <div className="ia-q-popup-header">
              <div className="ia-q-popup-title-row">
                <span className="ia-q-popup-num">{selectedStudent.student_name}</span>
                {selectedStudent.total_attempts > 1 && (
                  <span className="ia-attempt-badge">{selectedStudent.total_attempts} attempts</span>
                )}
                <span className="ia-attempt-progress-chip">
                  Best: {selectedStudent.best_completion_pct}%
                </span>
              </div>
              <button className="ia-q-popup-close" onClick={() => onSelectStudent(null)}>✕</button>
            </div>
            <div className="ia-student-responses">
              {(selectedStudent.attempts || []).map((attempt, attemptIdx) => (
                <CycleAttemptBlock
                  key={attempt.session_id}
                  attempt={attempt}
                  attemptNum={selectedStudent.attempts.length - attemptIdx}
                  cycleBreakdown={cycleBreakdown}
                  instructorId={instructorId}
                  onFlagsChange={onFlagsChange}
                  studentName={selectedStudent.student_name}
                  onAttemptDeleted={onFlagsChange}
                />
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Cycle detail popup — opens when a per-cycle card is clicked */}
      {selectedCycle && (
        <CycleDetailPopup
          cycle={selectedCycle}
          onClose={() => setSelectedCycle(null)}
        />
      )}
    </div>
  );
}

function CycleDetailPopup({ cycle, onClose }) {
  const hasReadSections = !!(cycle.read_sections && cycle.read_sections.trim());
  const coreIdeas = Array.isArray(cycle.core_ideas) ? cycle.core_ideas : [];
  const recommendations = Array.isArray(cycle.recommendations) ? cycle.recommendations : [];
  const questions = Array.isArray(cycle.questions) ? cycle.questions : [];
  const hasAny = hasReadSections || coreIdeas.length > 0 || recommendations.length > 0 || questions.length > 0;

  return (
    <div className="ia-q-popup-backdrop" onClick={onClose}>
      <div className="ia-q-popup ia-cycle-popup" onClick={e => e.stopPropagation()}>
        <div className="ia-q-popup-header">
          <div className="ia-q-popup-title-row">
            <span className="ia-cycle-num-badge ia-cycle-num-badge--lg">C{cycle.cycle_number}</span>
            <span className="ia-cycle-popup-title">{cycle.title}</span>
          </div>
          <button className="ia-q-popup-close" onClick={onClose}>✕</button>
        </div>

        {/* Class-engagement chips at the top */}
        <div className="ia-cycle-popup-chips">
          <span className="ia-cycle-stat"><strong>{cycle.reached}</strong> students reached</span>
          <span className={`ia-cycle-stat${cycle.stuck_here > 0 ? ' ia-cycle-stat--warn' : ''}`}>
            <strong>{cycle.stuck_here || '—'}</strong> stuck here
          </span>
          <span className="ia-cycle-stat"><strong>{cycle.avg_messages != null ? cycle.avg_messages : '—'}</strong> avg msgs</span>
          <span className="ia-cycle-stat"><strong>{cycle.reach_pct}%</strong> reach</span>
        </div>

        <div className="ia-cycle-popup-body">
          {!hasAny && (
            <p className="ia-conv-empty">No plan details available for this cycle.</p>
          )}
          {hasReadSections && (
            <div className="ia-cycle-popup-section">
              <div className="ia-cycle-popup-section-label">Read</div>
              <p className="ia-cycle-popup-read">{cycle.read_sections}</p>
            </div>
          )}
          {coreIdeas.length > 0 && (
            <div className="ia-cycle-popup-section">
              <div className="ia-cycle-popup-section-label">Core Ideas</div>
              <ol className="ia-cycle-popup-list">
                {coreIdeas.map((idea, i) => <li key={i}>{idea}</li>)}
              </ol>
            </div>
          )}
          {recommendations.length > 0 && (
            <div className="ia-cycle-popup-section">
              <div className="ia-cycle-popup-section-label">Guiding Recommendations</div>
              <ul className="ia-cycle-popup-list">
                {recommendations.map((rec, i) => <li key={i}>{rec}</li>)}
              </ul>
            </div>
          )}
          {questions.length > 0 && (
            <div className="ia-cycle-popup-section">
              <div className="ia-cycle-popup-section-label">Comprehension Questions</div>
              <div className="ia-cycle-popup-questions">
                {questions.map((q, i) => (
                  <div key={i} className="ia-cycle-popup-question">
                    <div className="ia-cycle-popup-q-head">
                      <span className="ia-cycle-popup-q-num">Q{q.number ?? i + 1}</span>
                      {q.type && (
                        <span className={`question-type-badge ${q.type === 'mcq' ? 'mcq' : q.type === 'counterfactual' ? 'counterfactual' : 'short-answer'}`}>
                          {q.type === 'mcq' ? 'MCQ' : q.type === 'counterfactual' ? 'Counterfactual' : 'Short Answer'}
                        </span>
                      )}
                    </div>
                    <p className="ia-cycle-popup-q-text">{q.question}</p>
                    {Array.isArray(q.options) && q.options.length > 0 && (
                      <ul className="ia-cycle-popup-q-options">
                        {q.options.map((opt, j) => <li key={j}>{opt}</li>)}
                      </ul>
                    )}
                    {q.answer && (
                      <div className="ia-cycle-popup-q-answer">
                        <span className="ia-cycle-popup-q-answer-label">Answer:</span> {q.answer}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}


// ── Q&A analytics ────────────────────────────────────────────────────────

function QAThreadBlock({ thread, studentUserId, paperId, instructorId, onChanged }) {
  const [expanded, setExpanded] = useState(false);
  const [messages, setMessages] = useState(null);
  const [flags, setFlags] = useState([]);
  const [aiFlags, setAiFlags] = useState([]);
  const [loading, setLoading] = useState(false);
  // Engagement scoring: auto score + rationale, plus the instructor's adjustment.
  const [autoScore, setAutoScore] = useState(thread.auto_score ?? null);
  const [autoRationale, setAutoRationale] = useState(thread.auto_rationale || '');
  const [adjusted, setAdjusted] = useState(thread.instructor_adjusted || false);
  const [score, setScore] = useState(thread.score ?? '');
  const [scoring, setScoring] = useState(false);
  const [note, setNote] = useState(thread.note || '');
  const [savingScore, setSavingScore] = useState(false);
  const [scanning, setScanning] = useState(false);
  const [scanResult, setScanResult] = useState(null); // {ok: bool, text: str} after a scan
  const [interjection, setInterjection] = useState('');
  const [sending, setSending] = useState(false);

  const loadFlags = async () => {
    try {
      const d = await getConversationFlags(thread.conversation_id);
      setFlags(d.flags || []);
      setAiFlags(d.ai_flags || []);
    } catch { /* ignore */ }
  };

  // Map a student message id → its AI-authorship flag, for the transcript overlay.
  const aiFlagsByMsgId = useMemo(() => {
    const m = {};
    for (const f of (aiFlags || [])) m[f.message_id] = f;
    return m;
  }, [aiFlags]);
  const activeAiFlagCount = (aiFlags || []).filter(f => f.status !== 'overridden').length;

  const handleAiFlagOverride = (flagId, newStatus) => {
    setAiFlags(prev => prev.map(f => (f.id === flagId ? { ...f, status: newStatus } : f)));
    onChanged?.();
  };

  // Fetch (or compute, cached) the auto engagement score. force=true recomputes.
  const runAutoScore = async (force = false) => {
    if (!instructorId) return;
    setScoring(true);
    try {
      const d = await autoScoreConversation(instructorId, thread.conversation_id, force);
      setAutoScore(d.auto_score);
      setAutoRationale(d.auto_rationale || '');
      setAdjusted(d.instructor_adjusted);
      // Prefill the editable field with the effective score if not yet adjusted.
      if (!d.instructor_adjusted) setScore(d.auto_score ?? '');
      onChanged?.();
    } finally { setScoring(false); }
  };

  const loadTranscript = async () => {
    setLoading(true);
    try {
      const [hist] = await Promise.all([
        getConversationHistory(studentUserId, paperId, thread.session_number, thread.chat_index),
        loadFlags(),
      ]);
      setMessages(hist.messages || []);
    } catch {
      setMessages([]);
    } finally {
      setLoading(false);
    }
  };

  const toggle = () => {
    const next = !expanded;
    setExpanded(next);
    if (next && messages == null) loadTranscript();
    // Auto-score on first open if it hasn't been scored yet — so the instructor
    // sees a score to adjust rather than a blank field.
    if (next && autoScore == null && !adjusted) runAutoScore(false);
  };

  const saveScore = async () => {
    if (!instructorId) return;
    setSavingScore(true);
    try {
      const val = score === '' ? null : Number(score);
      await scoreConversation(instructorId, thread.conversation_id, val, note);
      setAdjusted(true);
      onChanged?.();
    } finally { setSavingScore(false); }
  };

  const runScan = async () => {
    if (!instructorId) return;
    setScanning(true);
    setScanResult(null);
    try {
      const d = await scanHallucinations(instructorId, thread.conversation_id);
      const created = d?.created ?? 0;
      // Distinguish "nothing found this run" from "found N". Existing LLM flags
      // are skipped by the backend, so factor them into the "all clear" message.
      const existingOpen = (flags || []).filter(f => f.status !== 'dismissed').length;
      if (created > 0) {
        setScanResult({ ok: false, text: `Found ${created} potential hallucination${created !== 1 ? 's' : ''} — see flag${created !== 1 ? 's' : ''} below.` });
      } else if (existingOpen > 0) {
        setScanResult({ ok: true, text: 'No new hallucinations found. Existing flags are shown below.' });
      } else {
        setScanResult({ ok: true, text: '✓ No LLM hallucinations detected in this thread.' });
      }
      await loadFlags();
      onChanged?.();
    } catch {
      setScanResult({ ok: false, text: 'Scan failed. Please try again.' });
    } finally { setScanning(false); }
  };

  const sendInterjection = async () => {
    if (!instructorId || !interjection.trim()) return;
    setSending(true);
    try {
      await postInstructorMessage(instructorId, thread.conversation_id, interjection.trim());
      setInterjection('');
      if (expanded) await loadTranscript();
    } finally { setSending(false); }
  };

  return (
    <div className="ia-qa-thread">
      <button type="button" className="ia-cycle-conv-head" onClick={toggle} aria-expanded={expanded}>
        <span className="ia-qa-thread-title">{thread.title}</span>
        <span className="ia-cycle-conv-stats">
          <span className="ia-cycle-stat"><strong>{thread.student_msgs}</strong> student</span>
          <span className="ia-cycle-stat"><strong>{thread.ai_msgs}</strong> AI</span>
          {thread.score != null && <span className="ia-qa-score-chip">{thread.score}/100</span>}
          {thread.flag_count > 0 && <span className="ia-flag-badge">{thread.flag_count} hallucination flag{thread.flag_count !== 1 ? 's' : ''}</span>}
          {(aiFlags.length ? activeAiFlagCount : (thread.ai_flag_count || 0)) > 0 && (
            <span className="ia-flag-badge ia-flag-badge--ai">
              ⚑ {aiFlags.length ? activeAiFlagCount : thread.ai_flag_count} AI-use
            </span>
          )}
        </span>
        <span className="ia-cycle-conv-chevron">{expanded ? '▲' : '▼'}</span>
      </button>

      {expanded && (
        <div className="ia-qa-thread-body">
          {/* Engagement scoring: auto score the instructor can adjust */}
          <div className="ia-qa-controls">
            <div className="ia-qa-engagement">
              <div className="ia-qa-engagement-head">
                <span className="ia-qa-engagement-label">Engagement score</span>
                {scoring ? (
                  <span className="ia-qa-auto-pending">Scoring engagement…</span>
                ) : autoScore != null ? (
                  <span className="ia-qa-auto-chip" title="PaperBuddy's auto-assessed engagement score">
                    Auto: {autoScore}/100{adjusted ? ` · Adjusted: ${score === '' ? '—' : score}` : ''}
                  </span>
                ) : (
                  <span className="ia-qa-auto-pending">Not scored yet</span>
                )}
                <button
                  type="button" className="ia-flag-msg-btn"
                  disabled={scoring || !instructorId}
                  onClick={() => runAutoScore(true)}
                  title="Re-run the AI engagement score"
                >
                  ↻ Re-score
                </button>
              </div>
              {autoRationale && <p className="ia-qa-auto-rationale">{autoRationale}</p>}
            </div>
            <div className="ia-qa-score-row">
              <label className="ia-qa-label">Adjust</label>
              <input
                type="number" min="0" max="100" className="ia-qa-score-input"
                value={score} placeholder={autoScore != null ? String(autoScore) : '—'}
                onChange={e => setScore(e.target.value)}
              />
              <input
                type="text" className="ia-qa-note-input" placeholder="Score note (optional)"
                value={note} onChange={e => setNote(e.target.value)}
              />
              <button type="button" className="ia-hflag-btn" disabled={savingScore || !instructorId} onClick={saveScore}>
                {savingScore ? 'Saving…' : 'Save score'}
              </button>
            </div>

            <div className="ia-qa-action-row">
              <button type="button" className="ia-insights-btn" disabled={scanning || !instructorId} onClick={runScan}>
                {scanning ? 'Scanning…' : '✦ Scan for LLM hallucinations'}
              </button>
              {scanResult && (
                <span className={`ia-scan-result ${scanResult.ok ? 'ia-scan-result--clear' : 'ia-scan-result--found'}`}>
                  {scanResult.text}
                </span>
              )}
            </div>

            <div className="ia-qa-interject">
              <textarea
                className="ia-msg-flag-input" rows={2}
                placeholder="Message the student (they will see this in their thread)…"
                value={interjection} onChange={e => setInterjection(e.target.value)}
              />
              <button type="button" className="ia-hflag-btn" disabled={sending || !interjection.trim() || !instructorId} onClick={sendInterjection}>
                {sending ? 'Sending…' : 'Message student'}
              </button>
            </div>
          </div>

          {loading && <p className="ia-conv-loading">Loading transcript…</p>}
          {!loading && messages && messages.length === 0 && (
            <p className="ia-conv-empty">No messages in this thread.</p>
          )}
          {!loading && messages && messages.length > 0 && (
            <TranscriptMessages
              messages={messages}
              flags={flags}
              instructorId={instructorId}
              onFlagsChange={() => { loadFlags(); onChanged?.(); }}
              aiFlagsByMsgId={aiFlagsByMsgId}
              onAiFlagOverride={handleAiFlagOverride}
              aiFlagOverrideFn={overrideQaAiFlag}
            />
          )}
        </div>
      )}
    </div>
  );
}

function QAStudentPopup({ student, paperId, instructorId, onClose, onChanged }) {
  return (
    <div className="ia-q-popup-backdrop" onClick={onClose}>
      <div className="ia-q-popup ia-student-popup" onClick={e => e.stopPropagation()}>
        <div className="ia-q-popup-header">
          <div className="ia-q-popup-title-row">
            <span className="ia-q-popup-num">{student.student_name}</span>
            <span className="ia-attempt-badge">{student.thread_count} thread{student.thread_count !== 1 ? 's' : ''}</span>
            {student.avg_score != null && <span className="ia-qa-score-chip">avg {student.avg_score}/100</span>}
          </div>
          <button className="ia-q-popup-close" onClick={onClose}>✕</button>
        </div>
        <div className="ia-student-responses">
          {['plain', 'marker'].map(group => {
            const rows = (student.threads || []).filter(
              t => (group === 'marker') === Boolean(t.annotation_id));
            if (!rows.length) return null;
            return (
              <div key={group} className="ia-thread-group">
                <h5 className="ia-thread-group-title">
                  {group === 'marker' ? 'From your annotations' : 'Q&A'}
                  <span className="ia-thread-group-count">{rows.length}</span>
                </h5>
                {rows.map(t => (
                  <QAThreadBlock
                    key={t.conversation_id}
                    thread={t}
                    studentUserId={student.user_id}
                    paperId={paperId}
                    instructorId={instructorId}
                    onChanged={onChanged}
                  />
                ))}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

function QAAnalyticsView({ loading, data, paperId, instructorId, selectedStudent, onSelectStudent, onChanged }) {
  if (loading) return <div className="ia-loading">Loading Q&A analytics…</div>;
  if (!data) return <div className="ia-loading">No Q&A data available.</div>;

  const students = data.students || [];

  return (
    <div className="ia-scroll">
      <div className="ia-stat-row">
        <StatCard label="Students Engaged" value={data.total_students} accent={data.total_students > 0} />
        <StatCard label="Avg Msgs / Student" value={data.avg_messages_per_student} />
        <StatCard label="LLM Flags" value={data.llm_flag_count} hint="Potential PaperBuddy hallucinations auto-detected in AI replies." />
        <StatCard
          label="AI-use Flags"
          value={data.ai_flag_count || 0}
          accent={(data.ai_flag_count || 0) > 0}
          hint="Student messages the detector judged likely written by an AI (e.g. pasted from ChatGPT) rather than the student. Open a thread to review the confidence and override false positives."
        />
        <StatCard
          label="Avg Q&A Score"
          value={data.avg_qa_score != null ? `${data.avg_qa_score}` : '—'}
          hint="Average engagement score (0–100) across scored Q&A threads. PaperBuddy auto-scores how genuinely each student engaged; you can adjust any score."
        />
      </div>

      {data.llm_flag_count > 0 && (() => {
        const flaggedThreads = students.reduce((n, s) => n + (s.threads || []).filter(t => t.flag_count > 0).length, 0);
        return (
          <div className="ia-hallu-banner">
            <span className="ia-hallu-banner-icon">⚑</span>
            <span>
              <strong>{data.llm_flag_count} potential PaperBuddy hallucination{data.llm_flag_count !== 1 ? 's' : ''}</strong>
              {flaggedThreads > 0 && <> flagged across {flaggedThreads} thread{flaggedThreads !== 1 ? 's' : ''}</>}.
              {' '}Open a flagged student thread (⚑ badge) to review, confirm, or dismiss.
            </span>
          </div>
        );
      })()}

      {students.length === 0 ? (
        <div className="ia-empty">No students have used Q&A mode for this paper yet.</div>
      ) : (
        <div className="ia-section">
          <h4 className="ia-section-title">Student activity</h4>
          <p className="ia-section-hint">What students asked PaperBuddy. Engagement is auto-scored and new activity is auto-scanned for hallucinations each time you open analytics. Click a student to read transcripts, adjust scores, review flags, or message them.</p>
          <table className="ia-student-table">
            <thead>
              <tr>
                <th>Student</th>
                <th className="ia-td-center">Threads</th>
                <th className="ia-td-center">Student msgs</th>
                <th className="ia-td-center">AI msgs</th>
                <th className="ia-td-center">Score</th>
                <th className="ia-td-center" title="Potential PaperBuddy hallucinations">Hallucinations</th>
                <th className="ia-td-center" title="Student messages flagged as likely AI-written">AI-use</th>
              </tr>
            </thead>
            <tbody>
              {students.map((s, i) => (
                <tr key={s.user_id ?? i} className="ia-student-row ia-q-row--clickable" onClick={() => onSelectStudent(s)}>
                  <td className="ia-student-name"><span className="ia-expand-icon">▸</span>{s.student_name}</td>
                  <td className="ia-td-center">{s.thread_count}</td>
                  <td className="ia-td-center">{s.student_msg_count}</td>
                  <td className="ia-td-center">{s.ai_msg_count}</td>
                  <td className="ia-td-center">{s.avg_score != null ? <span className="ia-qa-score-chip">{s.avg_score}</span> : '—'}</td>
                  <td className="ia-td-center">{s.flag_count > 0 ? <span className="ia-flag-badge">{s.flag_count}</span> : '—'}</td>
                  <td className="ia-td-center">{s.ai_flag_count > 0 ? <span className="ia-flag-badge ia-flag-badge--ai">⚑ {s.ai_flag_count}</span> : '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {selectedStudent && (
        <QAStudentPopup
          student={selectedStudent}
          paperId={paperId}
          instructorId={instructorId}
          onClose={() => onSelectStudent(null)}
          onChanged={onChanged}
        />
      )}
    </div>
  );
}

export default function InstructorAnalytics({ paper, onBack }) {
  const [analyticsMode, setAnalyticsMode] = useState('test'); // 'test' | 'cycle' | 'qa'
  const [refreshKey, setRefreshKey] = useState(0);
  const instructorId = useMemo(() => getCurrentUserId(), []);
  const [qaData, setQaData] = useState(null);
  const [loadingQa, setLoadingQa] = useState(false);
  const [selectedQaStudent, setSelectedQaStudent] = useState(null);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [cycleData, setCycleData] = useState(null);
  const [loadingCycle, setLoadingCycle] = useState(false);
  const [selectedCyclePlanId, setSelectedCyclePlanId] = useState(null);
  const [selectedCycleStudent, setSelectedCycleStudent] = useState(null);
  const [insights, setInsights] = useState(null);
  const [loadingInsights, setLoadingInsights] = useState(false);
  const [cycleInsights, setCycleInsights] = useState(null);
  const [loadingCycleInsights, setLoadingCycleInsights] = useState(false);
  const [showCycleInsightsContent, setShowCycleInsightsContent] = useState(true);
  const [priorities, setPriorities] = useState(null);
  const [loadingPriorities, setLoadingPriorities] = useState(false);
  const [showPrioritiesContent, setShowPrioritiesContent] = useState(true);
  const [cyclePriorities, setCyclePriorities] = useState(null);
  const [loadingCyclePriorities, setLoadingCyclePriorities] = useState(false);
  const [showCyclePrioritiesContent, setShowCyclePrioritiesContent] = useState(true);
  const [showInsightsContent, setShowInsightsContent] = useState(true);
  const [sortBy, setSortBy] = useState('question'); // 'question' | 'score' | 'struggle'
  const [showUnansweredQs, setShowUnansweredQs] = useState(false);
  const [selectedQuestion, setSelectedQuestion] = useState(null);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [selectedPlanId, setSelectedPlanId] = useState(null);

  useEffect(() => {
    if (analyticsMode !== 'test') return;
    setLoading(true);
    setData(null);
    getInstructorAnalytics(paper.id, selectedPlanId)
      .then(d => {
        setData(d);
      })
      .catch(() => setData({ students: [], question_summary: [], class_average: null, total_students: 0, total_ai_flags: 0, total_hints_used: 0, plans: [], per_test_breakdown: [] }))
      .finally(() => setLoading(false));
  }, [paper.id, selectedPlanId, analyticsMode, refreshKey]);

  useEffect(() => {
    if (analyticsMode !== 'cycle') return;
    setLoadingCycle(true);
    setCycleData(null);
    getInstructorCycleAnalytics(paper.id, selectedCyclePlanId)
      .then(d => {
        setCycleData(d);
      })
      .catch(() => setCycleData({ students: [], cycle_breakdown: [], avg_completion_pct: null, total_students: 0, total_completed_runs: 0, total_ai_flags: 0, plans: [], per_plan_breakdown: [] }))
      .finally(() => setLoadingCycle(false));
  }, [paper.id, selectedCyclePlanId, analyticsMode, refreshKey]);

  useEffect(() => {
    if (analyticsMode !== 'qa') return;
    setLoadingQa(true);
    getInstructorQAAnalytics(paper.id)
      .then(d => setQaData(d))
      .catch(() => setQaData({ students: [], total_students: 0, total_threads: 0, total_messages: 0, avg_messages_per_student: 0, avg_qa_score: null, llm_flag_count: 0 }))
      .finally(() => setLoadingQa(false));
  }, [paper.id, analyticsMode, refreshKey]);

  // Keep the open cycle student popup + overview counts in sync after an AI-flag
  // override, without flashing the whole view back to a loading state.
  const reloadCycle = () => {
    getInstructorCycleAnalytics(paper.id, selectedCyclePlanId)
      .then(d => {
        setCycleData(d);
        // Re-point the open popup at the refreshed student; close it (null) if the
        // student no longer has any attempts (e.g. their last attempt was deleted).
        setSelectedCycleStudent(prev => prev ? (d.students || []).find(s => s.user_id === prev.user_id) || null : prev);
      })
      .catch(() => {});
  };

  // Keep the open test student popup + overview counts in sync after an attempt
  // deletion, without flashing the whole view back to a loading state.
  const reloadTest = () => {
    getInstructorAnalytics(paper.id, selectedPlanId)
      .then(d => {
        setData(d);
        setSelectedStudent(prev => prev ? (d.students || []).find(s => s.user_id === prev.user_id) || null : prev);
      })
      .catch(() => {});
  };

  // Keep the open student popup in sync after a score/flag/message mutation.
  const reloadQa = () => {
    getInstructorQAAnalytics(paper.id)
      .then(d => {
        setQaData(d);
        setSelectedQaStudent(prev => prev ? (d.students || []).find(s => s.user_id === prev.user_id) || prev : prev);
      })
      .catch(() => {});
  };

  const handleGenerateInsights = async () => {
    if (!data || loadingInsights) return;
    setShowInsightsContent(true);
    setLoadingInsights(true);
    try {
      const result = await generateInsights(paper.id, { ...data, mode: 'test' });
      setInsights(result.insights);
    } catch (e) {
      setInsights('Failed to generate insights. Please try again.');
    } finally {
      setLoadingInsights(false);
    }
  };

  const handleGenerateCycleInsights = async () => {
    if (!cycleData || loadingCycleInsights || !(cycleData.students?.length)) return;
    setShowCycleInsightsContent(true);
    setLoadingCycleInsights(true);
    try {
      const result = await generateInsights(paper.id, {
        ...cycleData,
        mode: 'cycle',
        selected_plan_id: selectedCyclePlanId,
      });
      setCycleInsights(result.insights);
    } catch (e) {
      setCycleInsights('Failed to generate insights. Please try again.');
    } finally {
      setLoadingCycleInsights(false);
    }
  };

  const handleGeneratePriorities = async () => {
    if (!data || loadingPriorities) return;
    setShowPrioritiesContent(true);
    setLoadingPriorities(true);
    try {
      const result = await generateTopicPriorities(paper.id, data.question_summary);
      setPriorities(result.priorities || []);
    } catch (e) {
      setPriorities([]);
    } finally {
      setLoadingPriorities(false);
    }
  };

  const hasPrioritiesContent = (priorities?.length ?? 0) > 0 && !loadingPriorities;

  const handleGenerateCyclePriorities = async () => {
    if (!cycleData || loadingCyclePriorities) return;
    const breakdown = cycleData.cycle_breakdown || [];
    if (!breakdown.length) return;
    setShowCyclePrioritiesContent(true);
    setLoadingCyclePriorities(true);
    try {
      const result = await generateCycleTopicPriorities(paper.id, breakdown);
      setCyclePriorities(result.priorities || []);
    } catch (e) {
      setCyclePriorities([]);
    } finally {
      setLoadingCyclePriorities(false);
    }
  };

  const hasCyclePrioritiesContent = (cyclePriorities?.length ?? 0) > 0 && !loadingCyclePriorities;

  // "All tests" mode: aggregated view across every test for this paper.
  // Question-level analytics don't make sense here (different tests have
  // different questions), so we swap in test-level comparisons instead.
  const isAllTests = !loading && data?.selected_plan_id == null && (data?.plans?.length || 0) > 0;

  const sortedQuestions = data ? [...(data.question_summary || [])].sort((a, b) => {
    if (sortBy === 'score') return a.average_score - b.average_score;
    if (sortBy === 'score-high') return b.average_score - a.average_score;
    if (sortBy === 'struggle') return b.weak_count - a.weak_count;
    return a.question_number - b.question_number;
  }) : [];

  // For a question bank, each student answers only a sampled subset, so most of
  // the bank's questions have no attempts. Collapse those into a dropdown so the
  // table shows only what students actually answered.
  const answeredQuestions = data?.is_bank ? sortedQuestions.filter(q => q.total_attempts > 0) : sortedQuestions;
  const unansweredQuestions = data?.is_bank ? sortedQuestions.filter(q => !(q.total_attempts > 0)) : [];

  const renderQuestionRow = (q) => (
    <tr
      key={q.question_number}
      className={`ia-q-row${q.average_score < 70 && q.total_attempts > 0 ? ' ia-q-row--warn' : ''}${q.question_text ? ' ia-q-row--clickable' : ''}`}
      onClick={() => q.question_text && setSelectedQuestion(q)}
      title={q.question_text ? 'Click to view question' : undefined}
    >
      <td className="ia-q-num">Q{q.question_number}</td>
      <td>
        {q.total_attempts > 0
          ? <span className={`ia-grade-badge ${gradeClass(q.average_score)}`}>{q.average_score}%</span>
          : <span className="ia-no-data">N/A</span>}
      </td>
      <td className="ia-q-bar-cell">
        <QBar strong={q.strong_count} partial={q.partial_count} weak={q.weak_count} total={q.total_attempts} />
      </td>
      <td className="ia-td-center ia-strong-text">{q.strong_count}</td>
      <td className="ia-td-center ia-partial-text">{q.partial_count}</td>
      <td className="ia-td-center ia-weak-text">{q.weak_count}</td>
    </tr>
  );

  // Score distribution buckets
  const scoreDistribution = data ? (() => {
    const buckets = [
      { label: 'A  90–100', min: 90, max: 100, count: 0 },
      { label: 'B  80–89',  min: 80, max: 89,  count: 0 },
      { label: 'C  70–79',  min: 70, max: 79,  count: 0 },
      { label: 'D  60–69',  min: 60, max: 69,  count: 0 },
      { label: 'F  <60',    min: 0,  max: 59,  count: 0 },
    ];
    (data.students || []).forEach(s => {
      if (s.average_grade == null) return;
      for (const b of buckets) {
        if (s.average_grade >= b.min && s.average_grade <= b.max) { b.count++; break; }
      }
    });
    return buckets;
  })() : [];

  // Question-type breakdown
  const typeBreakdown = data ? (() => {
    const map = {};
    (data.question_summary || []).forEach(q => {
      const t = q.question_type || 'unknown';
      if (!map[t]) map[t] = { scores: [], strong: 0, partial: 0, weak: 0 };
      map[t].scores.push(q.average_score);
      map[t].strong += q.strong_count;
      map[t].partial += q.partial_count;
      map[t].weak += q.weak_count;
    });
    return Object.entries(map).map(([type, d]) => ({
      type,
      avg: Math.round(d.scores.reduce((a, b) => a + b, 0) / d.scores.length),
      count: d.scores.length,
      strong: d.strong, partial: d.partial, weak: d.weak,
    }));
  })() : [];

  return (
    <div className="ia-panel">
      {/* Header */}
      <div className="ia-header">
        <div className="ia-header-left">
          <div className="ia-header-title-row">
            <h3 className="ia-title">Class analytics</h3>
          </div>
        </div>
        <span className="ia-paper-name">{paper.name}</span>
        <div className="ia-header-controls">
          <div className="ia-mode-segment" role="tablist" aria-label="Analytics mode">
            <button
              type="button"
              role="tab"
              aria-selected={analyticsMode === 'test'}
              className={`ia-mode-btn${analyticsMode === 'test' ? ' ia-mode-btn--active' : ''}`}
              onClick={() => setAnalyticsMode('test')}
            >
              Test
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={analyticsMode === 'cycle'}
              className={`ia-mode-btn${analyticsMode === 'cycle' ? ' ia-mode-btn--active' : ''}`}
              onClick={() => setAnalyticsMode('cycle')}
            >
              Cycle
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={analyticsMode === 'qa'}
              className={`ia-mode-btn${analyticsMode === 'qa' ? ' ia-mode-btn--active' : ''}`}
              onClick={() => setAnalyticsMode('qa')}
            >
              Q&amp;A
            </button>
          </div>
          {analyticsMode === 'test' && (data?.plans?.length > 0 || data?.has_custom) && (
            <select
              className="ia-plan-select"
              value={selectedPlanId ?? ''}
              onChange={e => { const v = e.target.value; setSelectedPlanId(v === '' ? null : v === 'custom' ? 'custom' : Number(v)); }}
            >
              <option value="">All tests</option>
              {data.plans.length > 0 && (
                <optgroup label="Official (instructor-published)">
                  {data.plans.map((p, i) => (
                    <option key={p.id} value={p.id}>
                      {i === 0 ? 'Latest · ' : ''}Test {data.plans.length - i} · {p.question_count}q
                    </option>
                  ))}
                </optgroup>
              )}
              {data.has_custom && (
                <optgroup label="Custom (student self-started)">
                  <option value="custom">Custom tests</option>
                </optgroup>
              )}
            </select>
          )}
          {analyticsMode === 'cycle' && (cycleData?.plans?.length > 0 || cycleData?.has_custom) && (
            <select
              className="ia-plan-select"
              value={selectedCyclePlanId ?? ''}
              onChange={e => { const v = e.target.value; setSelectedCyclePlanId(v === '' ? null : v === 'custom' ? 'custom' : Number(v)); }}
            >
              <option value="">All cycle plans</option>
              {cycleData.plans.length > 0 && (
                <optgroup label="Official (instructor-published)">
                  {cycleData.plans.map((p, i) => (
                    <option key={p.id} value={p.id}>
                      {i === 0 ? 'Latest · ' : ''}Plan {cycleData.plans.length - i} · {p.cycle_count}c
                    </option>
                  ))}
                </optgroup>
              )}
              {cycleData.has_custom && (
                <optgroup label="Custom (student self-started)">
                  <option value="custom">Custom cycles</option>
                </optgroup>
              )}
            </select>
          )}
          <button
            type="button"
            className="ia-refresh-btn"
            title="Refresh analytics"
            onClick={() => setRefreshKey(k => k + 1)}
          >
            ↻ Refresh
          </button>
        </div>
      </div>

      {analyticsMode === 'qa' ? (
        <QAAnalyticsView
          loading={loadingQa}
          data={qaData}
          paperId={paper.id}
          instructorId={instructorId}
          selectedStudent={selectedQaStudent}
          onSelectStudent={setSelectedQaStudent}
          onChanged={reloadQa}
        />
      ) : analyticsMode === 'cycle' ? (
        <CycleAnalyticsView
          loading={loadingCycle}
          data={cycleData}
          selectedPlanId={selectedCyclePlanId}
          onSelectPlan={setSelectedCyclePlanId}
          selectedStudent={selectedCycleStudent}
          onSelectStudent={setSelectedCycleStudent}
          insights={cycleInsights}
          loadingInsights={loadingCycleInsights}
          showInsightsContent={showCycleInsightsContent}
          onToggleInsightsShow={() => setShowCycleInsightsContent(v => !v)}
          onGenerateInsights={handleGenerateCycleInsights}
          priorities={cyclePriorities}
          loadingPriorities={loadingCyclePriorities}
          showPrioritiesContent={showCyclePrioritiesContent}
          hasPrioritiesContent={hasCyclePrioritiesContent}
          onTogglePrioritiesShow={() => setShowCyclePrioritiesContent(v => !v)}
          onGeneratePriorities={handleGenerateCyclePriorities}
          onExportCsv={() => exportCycleResultsCsv(paper.id)}
          onFlagsChange={reloadCycle}
        />
      ) : loading ? (
        <div className="ia-loading">Loading analytics…</div>
      ) : (
        <div className="ia-scroll">

          {/* Overview cards */}
          <div className="ia-stat-row">
            <StatCard
              label="Class Average"
              value={data.class_average != null ? `${data.class_average}%` : null}
              accent={data.class_average != null}
            />
            <StatCard label="Students Tested" value={data.total_students} />
            <StatCard label="AI Flags" value={data.total_ai_flags} />
            <StatCard
              label="Avg Hints / Student"
              value={data.total_students > 0 ? (data.total_hints_used / data.total_students).toFixed(1) : '—'}
            />
          </div>

          {data.total_students === 0 ? (
            <div className="ia-empty">No students have completed the test yet.</div>
          ) : (
            <>
              {/* Per-question performance — only in single-test mode */}
              {!isAllTests && (
                <div className="ia-section">
                  <div className="ia-section-header">
                    <h4 className="ia-section-title">Question Performance</h4>
                    <div className="ia-sort-btns">
                      <span className="ia-sort-label">Sort:</span>
                      {[['question', 'Q#'], ['score', 'Lowest Score'], ['score-high', 'Highest Score'], ['struggle', 'Most Struggled']].map(([val, label]) => (
                        <button key={val} type="button" className={`ia-sort-btn${sortBy === val ? ' active' : ''}`} onClick={() => setSortBy(val)}>{label}</button>
                      ))}
                    </div>
                  </div>
                  <table className="ia-q-table">
                    <thead>
                      <tr>
                        <th>Q#</th>
                        <th>Avg Score</th>
                        <th>Distribution</th>
                        <th>Strong</th>
                        <th>Partial</th>
                        <th>Weak</th>
                      </tr>
                    </thead>
                    <tbody>
                      {answeredQuestions.map(renderQuestionRow)}
                      {unansweredQuestions.length > 0 && (
                        <>
                          <tr
                            className="ia-q-unanswered-toggle-row"
                            onClick={() => setShowUnansweredQs(v => !v)}
                            title={showUnansweredQs ? 'Hide unanswered bank questions' : 'Show unanswered bank questions'}
                          >
                            <td colSpan={6} className="ia-q-unanswered-toggle">
                              {showUnansweredQs ? '▾' : '▸'} {unansweredQuestions.length} unanswered bank question{unansweredQuestions.length !== 1 ? 's' : ''} {showUnansweredQs ? '— hide' : '— show'}
                            </td>
                          </tr>
                          {showUnansweredQs && unansweredQuestions.map(renderQuestionRow)}
                        </>
                      )}
                    </tbody>
                  </table>
                </div>
              )}

              {/* Per-test breakdown — only in all-tests mode */}
              {isAllTests && (data.per_test_breakdown || []).length > 0 && (
                <div className="ia-section">
                  <h4 className="ia-section-title">Per-test class performance</h4>
                  <p className="ia-section-hint">How each test stacks up. Click a row to drill into its questions.</p>
                  <table className="ia-q-table ia-pertest-table">
                    <colgroup>
                      <col style={{ width: '22%' }} />
                      <col style={{ width: '13%' }} />
                      <col style={{ width: '19%' }} />
                      <col style={{ width: '19%' }} />
                      <col style={{ width: '13.5%' }} />
                      <col style={{ width: '13.5%' }} />
                    </colgroup>
                    <thead>
                      <tr>
                        <th>Test</th>
                        <th>Avg</th>
                        <th className="ia-td-center">Students</th>
                        <th className="ia-td-center">Attempts</th>
                        <th className="ia-td-center">Flags</th>
                        <th className="ia-td-center">Hints</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.per_test_breakdown.map(t => (
                        <tr
                          key={t.plan_id}
                          className="ia-q-row ia-q-row--clickable"
                          onClick={() => setSelectedPlanId(t.plan_id)}
                          title="Click to view this test's details"
                        >
                          <td className="ia-pertest-name">
                            <div className="ia-pertest-name-label">{t.plan_label}</div>
                            <div className="ia-pertest-name-meta">
                              {t.question_count} q{t.question_count !== 1 ? 's' : ''}
                            </div>
                          </td>
                          <td>
                            {t.class_average != null
                              ? <span className={`ia-grade-badge ${gradeClass(t.class_average)}`}>{t.class_average}%</span>
                              : <span className="ia-no-data">N/A</span>}
                          </td>
                          <td className="ia-td-center">{t.student_count || '—'}</td>
                          <td className="ia-td-center">{t.total_attempts || '—'}</td>
                          <td className="ia-td-center">{t.total_ai_flags > 0 ? <span className="ia-flag-badge">{t.total_ai_flags}</span> : '—'}</td>
                          <td className="ia-td-center">{t.total_hints_used || '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {/* Student breakdown */}
              <div className="ia-section">
                <div className="ia-section-header">
                  <h4 className="ia-section-title">Student Breakdown</h4>
                  <button
                    type="button"
                    className="ia-export-btn"
                    onClick={() => exportTestResultsCsv(paper.id)}
                    title="Download grades as CSV"
                  >
                    ↓ Export CSV
                  </button>
                </div>
                <p className="ia-section-hint">
                  {isAllTests
                    ? 'Overall performance across every test. Click a student to see their attempts grouped by test.'
                    : 'Click a student to see their responses.'}
                </p>
                <table className="ia-student-table ia-test-student-table">
                  <colgroup>
                    <col />
                    <col style={{ width: '5rem' }} />
                    {isAllTests && <col style={{ width: '3.5rem' }} />}
                    <col style={{ width: '3.5rem' }} />
                    <col style={{ width: '3.5rem' }} />
                    <col style={{ width: '5rem' }} />
                  </colgroup>
                  <thead>
                    <tr>
                      <th>Student</th>
                      <th>{isAllTests ? 'Best Avg' : 'High Score'}</th>
                      {isAllTests && <th className="ia-td-center">Tests</th>}
                      <th className="ia-td-center">Flags</th>
                      <th className="ia-td-center">Hints</th>
                      <th>Done</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.students.map((s, i) => (
                      <StudentRow key={s.session_id ?? i} student={s} onSelect={setSelectedStudent} isAllTests={isAllTests} />
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Score distribution */}
              {scoreDistribution.some(b => b.count > 0) && (
                <div className="ia-section">
                  <h4 className="ia-section-title">Score distribution</h4>
                  <div className="ia-dist-rows">
                    {scoreDistribution.map(b => (
                      <div key={b.label} className="ia-dist-row">
                        <span className="ia-dist-label">{b.label}</span>
                        <div className="ia-dist-track">
                          <div
                            className="ia-dist-fill"
                            style={{ width: data.total_students > 0 ? `${(b.count / data.total_students) * 100}%` : '0%' }}
                          />
                        </div>
                        <span className="ia-dist-count">{b.count}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Question-type breakdown — single-test only */}
              {!isAllTests && typeBreakdown.length > 1 && (
                <div className="ia-section">
                  <h4 className="ia-section-title">Performance by question type</h4>
                  <div className="ia-type-grid">
                    {typeBreakdown.map(t => (
                      <div key={t.type} className="ia-type-card">
                        <p className="ia-type-label">{t.type === 'mcq' ? 'MCQ' : t.type === 'short-answer' ? 'Short answer' : t.type === 'counterfactual' ? 'Counterfactual' : t.type}</p>
                        <p className="ia-type-avg">{t.avg}%</p>
                        <p className="ia-type-meta">{t.count} question{t.count !== 1 ? 's' : ''}</p>
                        <QBar strong={t.strong} partial={t.partial} weak={t.weak} total={t.strong + t.partial + t.weak} />
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Topic Priorities — single-test only (priorities run on a single test's questions) */}
              {!isAllTests && (
              <div className={`ia-section${hasPrioritiesContent && !showPrioritiesContent ? ' ia-section--collapsed' : ''}`}>
                <div className="ia-section-header">
                  <h4 className="ia-section-title">Teaching priorities</h4>
                  <div className="ia-section-actions">
                    {hasPrioritiesContent && (
                      <button
                        type="button"
                        className="ia-collapse-btn"
                        onClick={() => setShowPrioritiesContent(v => !v)}
                        aria-expanded={showPrioritiesContent}
                      >
                        {showPrioritiesContent ? 'Hide' : 'Show'}
                      </button>
                    )}
                    <button
                      type="button"
                      className="ia-insights-btn"
                      onClick={handleGeneratePriorities}
                      disabled={loadingPriorities}
                    >
                      {loadingPriorities ? 'Analyzing…' : hasPrioritiesContent ? '↺ Refresh' : '✦ Analyze with AI'}
                    </button>
                  </div>
                </div>
                {showPrioritiesContent && (
                  <>
                    {!priorities && !loadingPriorities && (
                      <p className="ia-insights-prompt">Identify which topics need the most attention before the next class.</p>
                    )}
                    {loadingPriorities && <div className="ia-insights-loading">Identifying priority topics…</div>}
                    {priorities && priorities.length === 0 && (
                      <p className="ia-insights-prompt">Could not generate priorities. Try again.</p>
                    )}
                    {priorities && priorities.length > 0 && (
                      <div className="ia-priorities-list">
                        {priorities.map((p, i) => (
                          <div key={i} className={`ia-priority-card ia-priority-card--${p.severity}`}>
                            <div className="ia-priority-header">
                              <span className={`ia-priority-badge ia-priority-badge--${p.severity}`}>
                                {p.severity === 'high' ? 'High' : p.severity === 'medium' ? 'Medium' : 'Low'}
                              </span>
                              <p className="ia-priority-topic">{p.topic}</p>
                            </div>
                            <p className="ia-priority-evidence">{p.evidence}</p>
                            <p className="ia-priority-action">→ {p.action}</p>
                          </div>
                        ))}
                      </div>
                    )}
                  </>
                )}
              </div>
              )}
            </>
          )}

          <AnalyticsInsightsPanel
            insights={insights}
            loadingInsights={loadingInsights}
            showInsightsContent={showInsightsContent}
            onToggleShow={() => setShowInsightsContent(v => !v)}
            onGenerate={handleGenerateInsights}
            generateDisabled={data.total_students === 0}
          />
        </div>
      )}

      {/* Student response popup */}
      {selectedStudent && (
        <div className="ia-q-popup-backdrop" onClick={() => setSelectedStudent(null)}>
          <div className="ia-q-popup ia-student-popup" onClick={e => e.stopPropagation()}>
            <div className="ia-q-popup-header">
              <div className="ia-q-popup-title-row">
                <span className="ia-q-popup-num">{selectedStudent.student_name}</span>
                {selectedStudent.total_attempts > 1 && (
                  <span className="ia-attempt-badge">{selectedStudent.total_attempts} attempts</span>
                )}
              </div>
              <button className="ia-q-popup-close" onClick={() => setSelectedStudent(null)}>✕</button>
            </div>
            <div className="ia-student-responses">
              {(selectedStudent.attempts || []).map((attempt, attemptIdx) => (
                <AttemptBlock
                  key={attempt.session_id}
                  attempt={attempt}
                  attemptNum={selectedStudent.attempts.length - attemptIdx}
                  defaultExpanded={attemptIdx === 0}
                  allQuestionInfos={data?.question_summary || []}
                  instructorId={instructorId}
                  studentName={selectedStudent.student_name}
                  onAttemptDeleted={reloadTest}
                  onFlagsChange={reloadTest}
                />
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Question detail popup */}
      {selectedQuestion && (
        <div className="ia-q-popup-backdrop" onClick={() => setSelectedQuestion(null)}>
          <div className="ia-q-popup" onClick={e => e.stopPropagation()}>
            <div className="ia-q-popup-header">
              <div className="ia-q-popup-title-row">
                <span className="ia-q-popup-num">Q{selectedQuestion.question_number}</span>
                {selectedQuestion.question_type && (
                  <span className={`question-type-badge ${selectedQuestion.question_type === 'mcq' ? 'mcq' : selectedQuestion.question_type === 'counterfactual' ? 'counterfactual' : 'short-answer'}`}>
                    {selectedQuestion.question_type === 'mcq' ? 'MCQ' : selectedQuestion.question_type === 'counterfactual' ? 'Counterfactual' : 'Short Answer'}
                  </span>
                )}
              </div>
              <button className="ia-q-popup-close" onClick={() => setSelectedQuestion(null)}>✕</button>
            </div>
            <p className="ia-q-popup-text">{selectedQuestion.question_text}</p>
            {selectedQuestion.question_options?.length > 0 && (
              <div className="ia-q-popup-options">
                {(() => {
                  // Support both single ("D") and comma-separated ("A, C") answer keys
                  const correctLetters = (selectedQuestion.question_answer || '')
                    .toUpperCase().split(/[,\s]+/).filter(l => /^[A-Z]$/.test(l));
                  // Guard: if ALL options are marked correct, the answer key is corrupted — show no highlights
                  const hasValidKey = correctLetters.length > 0 &&
                    correctLetters.length < selectedQuestion.question_options.length;
                  return selectedQuestion.question_options.map((opt, i) => {
                    // Prefer the letter embedded in the option text ("C. text" → 'C') over loop index
                    const prefixMatch = opt.trim().match(/^([A-Z])\./);
                    const letter = prefixMatch ? prefixMatch[1] : String.fromCharCode(65 + i);
                    const isCorrect = hasValidKey && correctLetters.includes(letter);
                    return (
                      <div key={i} className={`ia-q-popup-option${isCorrect ? ' correct' : ''}`}>
                        {opt}
                      </div>
                    );
                  });
                })()}
              </div>
            )}
            {selectedQuestion.question_answer && selectedQuestion.question_type !== 'mcq' && (
              <div className="ia-q-popup-answer">
                <span className="ia-q-popup-answer-label">Answer:</span> {selectedQuestion.question_answer}
              </div>
            )}
            {selectedQuestion.question_hints?.length > 0 && (
              <div className="ia-q-popup-hints">
                <p className="ia-q-popup-hints-label">Hints</p>
                {selectedQuestion.question_hints.map((h, i) => (
                  <p key={i} className="ia-q-popup-hint">
                    <span className="ia-q-popup-hint-num">H{i + 1}</span> {h}
                  </p>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
